import React, { useState, useEffect } from 'react';
import { Container, Typography, Button, Box, Grid, Card, CardContent, CardActions, Chip, Alert, CircularProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import api from '../api';

function Dashboard() {
  const navigate = useNavigate();
  const [disputes, setDisputes] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [disputesRes, statsRes] = await Promise.all([
        api.get('/disputes/'),
        api.get('/disputes/stats/')
      ]);
      setDisputes(disputesRes.data);
      setStats(statsRes.data);
    } catch (err) {
      setError('Failed to load your letters');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'default',
      sent: 'primary',
      replied: 'info',
      ignored: 'warning',
      bbb_filed: 'warning',
      ftc_filed: 'warning',
      chargeback_filed: 'warning',
      resolved: 'success',
      refunded: 'success',
      escalated: 'warning',
      abandoned: 'error',
    };
    return colors[status] || 'default';
  };
  
  const getStatusLabel = (status) => {
    const labels = {
      draft: 'Draft',
      sent: 'Sent',
      replied: 'Company Replied',
      ignored: 'Ignored (Escalated)',
      bbb_filed: 'BBB Filed',
      ftc_filed: 'FTC Filed',
      chargeback_filed: 'Chargeback Filed',
      resolved: 'Resolved',
      refunded: 'Refunded ✓',
      escalated: 'Escalated',
      abandoned: 'Abandoned',
    };
    return labels[status] || status.toUpperCase();
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'stretch', sm: 'center' }, 
        gap: 2,
        mb: 4 
      }}>
        <Typography variant="h4" sx={{ fontSize: { xs: '1.75rem', md: '2.125rem' } }}>Your Dispute Letters</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/dispute/new')}
          disabled={stats?.subscription?.can_create_more === false}
          sx={{ width: { xs: '100%', sm: 'auto' } }}
        >
          Create Letter
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Stats Cards */}
      {stats && (
        <Grid container spacing={{ xs: 2, md: 3 }} sx={{ mb: 4 }}>
          <Grid item xs={6} md={3}>
            <Card>
              <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                <Typography color="text.secondary" gutterBottom sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}>Total Letters</Typography>
                <Typography variant="h4" sx={{ fontSize: { xs: '1.75rem', md: '2.125rem' } }}>{stats.total_disputes}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card>
              <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                <Typography color="text.secondary" gutterBottom sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}>Success Rate</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="h4" sx={{ fontSize: { xs: '1.75rem', md: '2.125rem' } }}>{stats.success_rate}%</Typography>
                  <TrendingUpIcon sx={{ ml: 1, color: 'success.main', fontSize: { xs: '1.25rem', md: '1.5rem' } }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card>
              <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                <Typography color="text.secondary" gutterBottom sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}>Total Claimed</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <AttachMoneyIcon color="primary" sx={{ fontSize: { xs: '1.25rem', md: '1.5rem' } }} />
                  <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', md: '2.125rem' } }}>{(stats.total_claimed || 0).toFixed(2)}</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card>
              <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                <Typography color="text.secondary" gutterBottom sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}>Recovered</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <AttachMoneyIcon color="success" sx={{ fontSize: { xs: '1.25rem', md: '1.5rem' } }} />
                  <Typography variant="h4" color="success.main" sx={{ fontSize: { xs: '1.5rem', md: '2.125rem' } }}>{(stats.total_recovered || 0).toFixed(2)}</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Subscription Banner */}
      {stats?.subscription && !stats.subscription.can_create_more && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body2">
            You've used {stats.subscription.disputes_this_month} letters this month ({stats.subscription.plan} plan).{' '}
            <strong>Upgrade to Pro</strong> for 10 letters/month or <strong>Enterprise</strong> for unlimited.
          </Typography>
        </Alert>
      )}

      {/* Letters List */}
      {disputes.length === 0 ? (
        <Card sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>No letters yet</Typography>
          <Typography color="text.secondary" paragraph>
            Create your first AI-powered dispute letter and start getting results
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/dispute/new')}
          >
            Create Your First Letter
          </Button>
        </Card>
      ) : (
        <Grid container spacing={{ xs: 2, md: 3 }}>
          {disputes.map((dispute) => (
            <Grid item xs={12} key={dispute.id}>
              <Card>
                <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" sx={{ fontSize: { xs: '1.125rem', md: '1.25rem' } }}>{dispute.company}</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontSize: { xs: '0.875rem', md: '0.875rem' } }}>
                        {dispute.description.substring(0, { xs: 100, md: 150 }[window.innerWidth >= 600 ? 'md' : 'xs'])}...
                      </Typography>
                      <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Chip 
                          label={getStatusLabel(dispute.status)} 
                          size="small" 
                          color={getStatusColor(dispute.status)} 
                        />
                        {dispute.escalated_at && (
                          <Chip 
                            label="⚠️ ESCALATED" 
                            size="small" 
                            color="warning"
                            variant="outlined"
                          />
                        )}
                        {dispute.amount_claimed && (
                          <Chip 
                            label={`$${parseFloat(dispute.amount_claimed).toFixed(2)}`} 
                            size="small" 
                            icon={<AttachMoneyIcon />} 
                          />
                        )}
                        {dispute.amount_recovered && (
                          <Chip 
                            label={`Recovered: $${parseFloat(dispute.amount_recovered).toFixed(2)}`} 
                            size="small" 
                            color="success"
                            icon={<TrendingUpIcon />}
                          />
                        )}
                        <Chip 
                          label={new Date(dispute.created_at).toLocaleDateString()} 
                          size="small" 
                          variant="outlined" 
                        />
                      </Box>
                    </Box>
                  </Box>
                </CardContent>
                <CardActions>
                  <Button size="small" onClick={() => navigate(`/dispute/${dispute.id}`)}>
                    View Details
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Container>
  );
}

export default Dashboard;
